import { inject, Injectable } from '@angular/core';
import { catchError, from, merge, Observable, of, Subject } from 'rxjs';
import { filter, first, map, shareReplay, switchMap, tap } from 'rxjs/operators';
import { DOCUMENT } from '@angular/common';

import { ActivatedRoute, Router } from '@angular/router';
import { AppRoutingLogin, AppRoutingSelect } from '../../app-routing.model';
import {
  AuthCode,
  AuthResult,
  CookieService as AuthCookieService,
  FirebaseService,
} from '@movinmotion/data-access-auth';
import { AuthProviderName } from '../models/environment';
import { environment } from '../../../environments/environment';
import { LoggerService } from './logger.service';

import { AuthProvider, OAuthProvider, getIdToken } from '@angular/fire/auth';
import { CookieService } from 'ngx-cookie-service';
import passwordEntropy from 'fast-password-entropy';
import {
  CONNEXION_TYPE_COOKIE_NAME,
  CookieContent,
  ENVIRONMENT_CONFIG,
  MovinmotionAppEnum,
} from '@movinmotion/conf-shared';
import constants from '../constants';
import { AuthService as SharedAuthService } from '@movinmotion/data-access-auth';

export const queryParamKeyRedirectUrl = environment.queryParamsKeys.redirectUrl;

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly firebaseService = inject(FirebaseService);
  private readonly activeRoute = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly document = inject<Document>(DOCUMENT);
  private readonly cookieService = inject(CookieService);
  private readonly logger = inject(LoggerService);
  private readonly authService = inject(SharedAuthService);
  private readonly cookieAuthService = inject(AuthCookieService);

  protected environment = inject(ENVIRONMENT_CONFIG);
  private passwordEntropy: number | null = null;

  private readonly redirectUrl: Observable<URL | null>;
  private readonly customRedirectUrl = new Subject<URL | null>();

  protected readonly providers: { [key in AuthProviderName]?: AuthProvider } = Object.entries(
    environment.providers,
  ).reduce(
    (result, [name, { id, enabled }]) => {
      if (id && enabled) {
        result[name as AuthProviderName] = new OAuthProvider(id);
      }
      return result;
    },
    {} as { [key in AuthProviderName]?: AuthProvider },
  );

  constructor() {
    // The redirect URL merge the one provided by query parameter or the custom one specify directly
    this.redirectUrl = merge(
      of(null), // Initialise with null to emit at least one value
      merge(
        this.customRedirectUrl.asObservable(),
        this.activeRoute.queryParams.pipe(
          map(params => {
            return params?.[queryParamKeyRedirectUrl]
              ? this.convertStringToUrl(params[queryParamKeyRedirectUrl])
              : null;
          }),
        ),
      ).pipe(filter(url => url !== null)), // Avoid not usable URL
    ).pipe(shareReplay(1)); // Allow to retrieve the last emitted value
  }

  /**
   * Sign in the user using the email and password provided
   *
   * @param email of the user
   * @param password of the user
   */
  signInWithEmailAndPasswordWrapper$(email: string, password: string): Observable<AuthResult> {
    this.passwordEntropy = passwordEntropy(password);
    return this.firebaseService.signInWithEmailAndPassword$(email, password);
  }

  autoSignIn$(email: string, link: string): Observable<AuthResult> {
    return this.firebaseService.signInWithEmailLink$(email, link);
  }

  /**
   * Sing in the user using the provider specified (make a redirection)
   *
   * @param provider to use
   */
  signInWithProvider$(provider: AuthProviderName): Observable<void> {
    if (this.providers?.[provider]) {
      this.setConnectionCookie({ connectionMode: 'PROVIDER', provider });
      return this.firebaseService.signInWithRedirect$(this.providers[provider]);
    }
    const errorMsg = 'Provider "' + provider + '" is not declared or missing, unable to sign in';
    this.logger.error(errorMsg);
    throw new Error(errorMsg);
  }

  /**
   * Check if there is a response for authentication after a provider redirection {@see signInWithProvider}
   *
   * @return the authentication result if there is one, else null
   */
  signInWithProviderResult$(): Observable<AuthResult | null> {
    return this.firebaseService.getRedirectResult$();
  }

  goToRedirectUrl$(): Observable<void> {
    return this.redirectUrl.pipe(
      tap(url => {
        const isConnected = !!this.cookieAuthService.hasSessionCookie();
        if (isConnected && url) {
          this.document.location.href = url.toString();
        } else {
          const display = this.activeRoute.snapshot.queryParamMap.get(environment.queryParamsKeys.display);
          this.authService.redirectToLogin({ returnUrl: url?.toString(), display: display ?? undefined });
        }
      }),
      map(() => undefined),
    );
  }

  loginAndRedirect$(customRedirectUrl?: string): Observable<{ url: URL } | null> {
    if (customRedirectUrl) {
      this.customRedirectUrl.next(this.convertStringToUrl(customRedirectUrl));
    }

    if (!this.firebaseService.getCurrentUser()) {
      return of(null);
    }

    return this.firebaseService.getFirebaseToken().pipe(
      switchMap(token => this.redirectUrl.pipe(map(url => ({ token, url })))),
      switchMap(({ token, url }) => {
        if (!token) {
          return from(this.router.navigate([AppRoutingLogin], { queryParamsHandling: 'merge' })).pipe(map(() => null));
        }

        if (!url || !constants.redirectionAllowedDomains.some(domain => url.host.endsWith(domain))) {
          return from(this.router.navigate([AppRoutingSelect], { queryParamsHandling: 'merge' })).pipe(map(() => null));
        }

        // odalie quick-win to allow thalie-sante.org domain
        if (url.host.endsWith('thalie-sante.org')) {
          url.searchParams.set('mmt', token);
          if (this.passwordEntropy) {
            url.searchParams.set('mment', this.passwordEntropy.toString());
          }
          this.document.location.href = url.toString();

          return of(null);
        }

        const entropy = this.passwordEntropy;
        const appName = this.getMovinmotionAppFromUrl(url.toString());

        return this.authService
          .login$({
            token,
            entropy: entropy ?? undefined,
            appName: appName ?? undefined,
          })
          .pipe(map(() => ({ url })));
      }),
      switchMap(result => {
        if (result?.url) {
          this.document.location.href = result.url.toString();
        }

        return of(null);
      }),
    );
  }

  getMovinmotionAppFromUrl(url: string): MovinmotionAppEnum | null {
    if (!url) {
      return null;
    }

    try {
      const parsedUrl = new URL(url);
      const host = parsedUrl.hostname;
      const apps = this.environment.apps;

      const hostEnumMap: { [hostname: string]: MovinmotionAppEnum } = {
        [new URL(apps.admin).hostname]: MovinmotionAppEnum.ADMIN,
        [new URL(apps.worker).hostname]: MovinmotionAppEnum.WORKER,
        [new URL(apps.talents).hostname]: MovinmotionAppEnum.TALENTS,
        [new URL(apps.compta).hostname]: MovinmotionAppEnum.COMPTA,
        [new URL(apps.core).hostname]: MovinmotionAppEnum.CORE,
        [new URL(apps.social).hostname]: MovinmotionAppEnum.SOCIAL,
        [new URL(apps.socialPlay).hostname]: MovinmotionAppEnum.SOCIAL,
      };

      const appName = hostEnumMap[host] ?? null;

      /**
       * L'accès au parcours de souscription nécessite de bypass une verification actuellement présente coté backend, qui assure que toute demande de login à destination premiere de l'application CORE est faite par quelqu'un disposant à minima d'un accès "ADMIN" sur une entreprise.
       * Dans le cas du nouveau PS où l'utilisateur crée son compte puis se connecte à mi-parcours, il est alors impossible de valider cette condition. On qualifie alors l'app d'EMPLOYEUR.
       *
       * Le check coté backend est relativement "cosmétique" car il se base uniquement sur le appName envoyé par le front et on peut deja tricher en se connectant à Salarié puis rediriger par la suite sur entreprise.
       *
       * La classe en question coté back est la : com/movinmotion/core/login/chain/impl/CheckCoreAdminStep.java
       *
       * Il faudrait plutot se baser sur un guard dans le futur sur les applications employeur si on souhaite en bloquer l'accès aux non-employeurs
       *
       * https://movinmotion.atlassian.net/browse/MOV-18755
       */
      if (appName === MovinmotionAppEnum.CORE && parsedUrl.pathname.startsWith('/subscription')) {
        return MovinmotionAppEnum.EMPLOYEUR;
      }

      return appName;
    } catch (e) {
      console.error('URL invalide', e);
      return null;
    }
  }

  /**
   * Changes on the redirect URL stored
   */
  redirectUrlChanged$(): Observable<URL | null> {
    return this.redirectUrl;
  }

  /**
   * Manage automatically a sign in with a provider (handle redirection).
   * Just subscribe to this observable, that will provide a loading indicator and an authentication result if one come.
   * To trigger this authentication, there is two ways:
   *   - provide in the query param the provider name, {@see environment.queryParamsKeys.providerConnection} for the param value
   *   - call the {@see signInWithProvider} with the right provider
   */
  automaticProviderLogin$(): Observable<{ loading: boolean; authResult?: AuthResult }> {
    return this.signInWithProviderResult$().pipe(
      switchMap(authResult => {
        if (authResult && authResult.code === AuthCode.success) {
          return this.loginAndRedirect$().pipe(
            map(() => ({
              loading: true,
              authResult,
            })),
          );
        } else if (!authResult) {
          return this.activeRoute.queryParamMap.pipe(
            switchMap(params => {
              const connectionType = this.getConnectionTypeOnCookie();
              if (
                params.has(environment.queryParamsKeys.providerConnection) ||
                connectionType.connectionMode === 'PROVIDER'
              ) {
                const provider = params.has(environment.queryParamsKeys.providerConnection)
                  ? (params.get(environment.queryParamsKeys.providerConnection) as AuthProviderName)
                  : (connectionType.provider as AuthProviderName);
                try {
                  return this.signInWithProvider$(provider).pipe(
                    map(() => ({
                      loading: true,
                    })),
                  );
                } catch (err) {
                  // Noop
                }
              }
              return of({
                loading: false,
              });
            }),
          );
        } else {
          return of({
            loading: false,
            authResult,
          });
        }
      }),
      catchError(() => {
        return of({
          loading: false,
        });
      }),
    );
  }

  /**
   * Convert the provided string to a valid URL. If the URL is not valid, return null (and log a console error).
   *
   * @param url to convert
   */
  private convertStringToUrl(url: string): URL | null {
    try {
      return new URL(url);
    } catch (err) {
      this.logger.error('Unable to get a valid URL from the one provided, see error:', err);
    }
    return null;
  }

  private setConnectionCookie(cookieContent: CookieContent) {
    this.cookieService.set(CONNEXION_TYPE_COOKIE_NAME, JSON.stringify(cookieContent), {
      domain: window.location.host.split('.app.')[1],
      expires: new Date(new Date().getTime() + 48 * 60 * 60 * 1000),
      secure: true,
      sameSite: 'Lax',
    });
  }

  private getConnectionTypeOnCookie(): CookieContent {
    const cookieValue = this.cookieService.get(CONNEXION_TYPE_COOKIE_NAME);
    return JSON.parse(cookieValue && cookieValue.length ? cookieValue : '{}');
  }
}
